import React, { useState } from 'react';
import { Typography, Alert, Card, Button, Select, Space, Modal, message } from 'antd';
import { EyeOutlined, DownloadOutlined } from '@ant-design/icons';
import MolStarViewer from '../protein/structure/MolStarViewer';

const { Title, Text } = Typography;

interface ZdockResultProps {
  taskResult: any;
  data?: any;
  title?: string;
  showTitle?: boolean;
}

const ZdockResult: React.FC<ZdockResultProps> = ({ 
  taskResult, 
  data, 
  title = "ZDOCK 对接结果", 
  showTitle = true 
}) => {
  const [selectedPredictionIndex, setSelectedPredictionIndex] = useState(0);
  const [viewerVisible, setViewerVisible] = useState(false);
  const [selectedPdb, setSelectedPdb] = useState<string | null>(null);

  // 提取PDB内容
  const getPdbContentByIndex = (res: any, index: number): string | null => {
    if (!res) return null;
    
    // 检查统一格式：res.results[0].result.predictions[index].pdb_content (实际数据结构)
    if (res.results && Array.isArray(res.results) && res.results.length > 0) {
      const firstResult = res.results[0];
      if (firstResult?.result?.predictions && Array.isArray(firstResult.result.predictions) && firstResult.result.predictions.length > index) {
        return firstResult.result.predictions[index]?.pdb_content || null;
      }
    }
    
    // 检查嵌套格式：res.result.results[0].result.predictions[index].pdb_content
    if (res.result?.results && Array.isArray(res.result.results) && res.result.results.length > 0) {
      const zdockResult = res.result.results[0]?.result;
      if (zdockResult?.predictions && Array.isArray(zdockResult.predictions) && zdockResult.predictions.length > index) {
        return zdockResult.predictions[index]?.pdb_content || null;
      }
    }
    
    // 检查直接格式：predictions[index]
    if (res.predictions && Array.isArray(res.predictions) && res.predictions.length > index) {
      return res.predictions[index]?.pdb_content || null;
    }
    
    return null;
  };

  // 获取可用预测数量
  const getAvailablePredictions = (res: any): number => {
    if (!res) return 0;
    
    // 检查统一格式：res.results[0].result.predictions (实际数据结构)
    if (res.results && Array.isArray(res.results) && res.results.length > 0) {
      const firstResult = res.results[0];
      if (firstResult?.result?.predictions && Array.isArray(firstResult.result.predictions)) {
        return firstResult.result.predictions.length;
      }
    }
    
    // 检查嵌套格式：res.result.results[0].result.predictions
    if (res.result?.results && Array.isArray(res.result.results) && res.result.results.length > 0) {
      const zdockResult = res.result.results[0]?.result;
      if (zdockResult?.predictions && Array.isArray(zdockResult.predictions)) {
        return zdockResult.predictions.length;
      }
    }
    
    // 检查直接格式：predictions
    if (res.predictions && Array.isArray(res.predictions)) {
      return res.predictions.length;
    }
    
    return 0;
  };

  // 获取预测结果的详细信息
  const getPredictionInfo = (res: any, index: number): { rank?: number, score?: number } | null => {
    if (!res) return null;
    
    // 检查统一格式：res.results[0].result.predictions[index]
    if (res.results && Array.isArray(res.results) && res.results.length > 0) {
      const firstResult = res.results[0];
      if (firstResult?.result?.predictions && Array.isArray(firstResult.result.predictions) && firstResult.result.predictions.length > index) {
        const prediction = firstResult.result.predictions[index];
        return {
          rank: prediction?.rank,
          score: prediction?.score
        };
      }
    }
    
    // 检查嵌套格式：res.result.results[0].result.predictions[index]
    if (res.result?.results && Array.isArray(res.result.results) && res.result.results.length > 0) {
      const zdockResult = res.result.results[0]?.result;
      if (zdockResult?.predictions && Array.isArray(zdockResult.predictions) && zdockResult.predictions.length > index) {
        const prediction = zdockResult.predictions[index];
        return {
          rank: prediction?.rank,
          score: prediction?.score
        };
      }
    }
    
    // 检查直接格式：predictions[index]
    if (res.predictions && Array.isArray(res.predictions) && res.predictions.length > index) {
      const prediction = res.predictions[index];
      return {
        rank: prediction?.rank,
        score: prediction?.score
      };
    }
    
    return null;
  };

  // 处理预测切换
  const handlePredictionChange = (index: number) => {
    setSelectedPredictionIndex(index);
    
    const pdbContent = getPdbContentByIndex(taskResult, index);
    if (pdbContent && typeof pdbContent === 'string' && pdbContent.trim().length > 0) {
      setSelectedPdb(pdbContent);
    } else {
      message.error(`第 ${index + 1} 个预测结果无PDB内容`);
      setSelectedPdb(null);
    }
  };

  // 查看3D结构
  const handleView3D = () => {
    console.log('ZDOCK 调试信息:', {
      taskResult,
      selectedPredictionIndex,
      availablePredictions
    });
    
    const pdbContent = getPdbContentByIndex(taskResult, selectedPredictionIndex);
    console.log('提取的PDB内容:', {
      pdbContent: pdbContent ? pdbContent.substring(0, 200) + '...' : null,
      pdbLength: pdbContent ? pdbContent.length : 0
    });
    
    if (pdbContent && typeof pdbContent === 'string' && pdbContent.trim().length > 0) {
      setSelectedPdb(pdbContent);
      setViewerVisible(true);
    } else {
      message.error('暂无可视化PDB内容，请检查控制台调试信息');
    }
  };

  // 下载PDB
  const handleDownloadPdb = () => {
    const pdbContent = getPdbContentByIndex(taskResult, selectedPredictionIndex);
    if (pdbContent) {
      const blob = new Blob([pdbContent], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `zdock_prediction_${selectedPredictionIndex + 1}.pdb`;
      a.click();
      URL.revokeObjectURL(url);
    } else {
      message.error('暂无PDB内容可下载');
    }
  };

  const availablePredictions = getAvailablePredictions(taskResult);
  const currentPredictionInfo = getPredictionInfo(taskResult, selectedPredictionIndex);

  if (!taskResult) {
    return <Alert type="warning" message="无任务数据" />;
  }

  if (availablePredictions === 0) {
    return (
      <Alert
        type="info"
        message="无ZDOCK对接结果"
        description="任务可能还在运行中或者结果格式不正确"
        showIcon
      />
    );
  }

  return (
    <div>
      {showTitle && (
        <div style={{ marginBottom: 16 }}>
          <Text strong>{title}</Text>
          <Text type="secondary" style={{ marginLeft: 8 }}>({availablePredictions} 个预测结果)</Text>
        </div>
      )}

      <Card>
        <div style={{ marginBottom: 16 }}>
          <Space align="center">
            <Text strong>选择预测结果:</Text>
            <Select
              value={selectedPredictionIndex}
              onChange={handlePredictionChange}
              style={{ width: 200 }}
              placeholder="选择预测结果"
            >
              {Array.from({ length: availablePredictions }, (_, index) => {
                const info = getPredictionInfo(taskResult, index);
                return (
                  <Select.Option key={index} value={index}>
                    预测 {index + 1} {info?.score ? `(评分: ${info.score.toFixed(3)})` : ''}
                  </Select.Option>
                );
              })}
            </Select>
            <Text type="secondary">
              共 {availablePredictions} 个预测结果
            </Text>
          </Space>
        </div>

        {/* 显示当前预测的详细信息 */}
        {currentPredictionInfo && (
          <div style={{ marginBottom: 16, padding: '12px', backgroundColor: '#f5f5f5', borderRadius: '6px' }}>
            <Space size="large">
              <Text><strong>排名:</strong> {currentPredictionInfo.rank || selectedPredictionIndex + 1}</Text>
              <Text><strong>ZDOCK评分:</strong> {currentPredictionInfo.score ? currentPredictionInfo.score.toFixed(3) : 'N/A'}</Text>
            </Space>
          </div>
        )}

        <Space>
          <Button 
            type="primary" 
            icon={<EyeOutlined />}
            onClick={handleView3D}
          >
            查看3D结构
          </Button>
          <Button 
            icon={<DownloadOutlined />}
            onClick={handleDownloadPdb}
          >
            下载PDB
          </Button>
        </Space>
      </Card>

      {/* 3D结构查看器模态框 */}
      <Modal
        title={`ZDOCK 预测结果 ${selectedPredictionIndex + 1} - 3D结构`}
        open={viewerVisible}
        onCancel={() => setViewerVisible(false)}
        footer={null}
        width={1000}
        style={{ top: 20 }}
      >
        {selectedPdb && (
          <div style={{ height: 600 }}>
            <MolStarViewer 
              pdbData={selectedPdb} 
              title={`ZDOCK 预测 ${selectedPredictionIndex + 1}`}
              height={580}
            />
          </div>
        )}
      </Modal>
    </div>
  );
};

export default ZdockResult;
